export const SITE_NAME = 'MEZZANINE';
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://mezzaninestudios.com').replace(/\/$/, '');
export const SITE_DESCRIPTION =
  'MEZZANINE is an artist-run non-profit art space in south-east London, supporting artists with affordable studios, exhibitions and events.';
export const SITE_ADDRESS = {
  streetAddress: 'Unit 15 Orion Business Centre',
  addressLocality: 'London',
  postalCode: 'SE14 5RT',
  addressCountry: 'GB'
};

export function toAbsoluteUrl(value) {
  if (!value) return '';
  try {
    return new URL(value, `${SITE_URL}/`).href;
  } catch {
    return '';
  }
}

export function toPlainText(value = '') {
  return value
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function truncateText(value, maxLength = 180) {
  const text = toPlainText(value);
  if (text.length <= maxLength) return text;

  const shortened = text.slice(0, maxLength + 1);
  const lastSpace = shortened.lastIndexOf(' ');
  return `${shortened.slice(0, lastSpace > maxLength * 0.7 ? lastSpace : maxLength).trim()}…`;
}

export function eventSlug(event) {
  if (event.slug) return event.slug;

  const titleSlug = event.title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

  return `${titleSlug}-${event.id}`;
}

export function londonDateTime(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || '');
  if (!match || Number(match[1]) < 2000) return undefined;

  const seconds = match[6] || '00';
  const localDateTime = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${seconds}`;
  const provisionalDate = new Date(`${localDateTime}Z`);
  if (Number.isNaN(provisionalDate.getTime())) return undefined;

  const zoneName = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    timeZoneName: 'longOffset'
  })
    .formatToParts(provisionalDate)
    .find((part) => part.type === 'timeZoneName')?.value;

  const offset = zoneName?.replace('GMT', '') || '+00:00';
  return `${localDateTime}${offset === '' ? '+00:00' : offset}`;
}

export function createHomeStructuredData(imageUrl) {
  const organizationId = `${SITE_URL}/#organization`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        description: SITE_DESCRIPTION,
        inLanguage: 'en-GB',
        publisher: { '@id': organizationId }
      },
      {
        '@type': 'ArtGallery',
        '@id': organizationId,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        description: SITE_DESCRIPTION,
        email: 'contact.mezzaninestudio@gmail.com',
        image: imageUrl || undefined,
        address: {
          '@type': 'PostalAddress',
          ...SITE_ADDRESS
        },
        sameAs: ['https://instagram.com/mezzanine_studios/']
      }
    ]
  };
}

export function createEventSeo(event) {
  const slug = eventSlug(event);
  const canonicalUrl = `${SITE_URL}/events/${slug}/`;
  const imageUrl = toAbsoluteUrl(event.imageUrl);
  const description = truncateText(event.description || event.fullDescription || SITE_DESCRIPTION);
  const structuredDescription = toPlainText(event.fullDescription || event.description || SITE_DESCRIPTION);
  const startDate = londonDateTime(event.date);
  const endDate = londonDateTime(event.endDate);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ExhibitionEvent',
    '@id': `${canonicalUrl}#event`,
    name: event.title,
    description: structuredDescription,
    url: canonicalUrl,
    startDate,
    endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    image: imageUrl ? [imageUrl] : undefined,
    location: {
      '@type': 'Place',
      name: SITE_NAME,
      address: {
        '@type': 'PostalAddress',
        ...SITE_ADDRESS
      }
    },
    organizer: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: `${SITE_URL}/`
    },
    mainEntityOfPage: canonicalUrl
  };

  Object.keys(structuredData).forEach((key) => {
    if (structuredData[key] === undefined) delete structuredData[key];
  });

  return {
    title: `${event.title} | ${SITE_NAME}`,
    description,
    canonicalUrl,
    imageUrl,
    imageAlt: `${event.title} at ${SITE_NAME}`,
    structuredData
  };
}
